import { TFunction } from "i18next";
import { FieldFormat } from "../types";

export type FormatContext = {
  t: TFunction;
  resolvers: Record<string, (value: unknown) => string>;
  mapVarToLabel: (value: string) => string;
};

/** Turns a raw property value into the same readable text the form shows,
 * following the field's declared format. */
export function formatValue(
  value: unknown,
  format: FieldFormat | undefined,
  context: FormatContext
): string {
  const { t } = context;

  if (value === null || value === undefined || value === "") {
    return t("common:empty");
  }

  if (!format || format === "text") return formatScalar(value, t);

  if (format === "boolean") {
    return t(`confirmRuleUpdates.booleanValue.${Boolean(value)}`);
  }
  if (format === "number") return String(value);

  if (format === "password") return "••••••••";

  if (format === "variableTokens") {
    return context.mapVarToLabel(String(value)) || String(value);
  }

  if (format === "folder") {
    const folder = value as { path?: string };
    return folder?.path ?? formatScalar(value, t);
  }

  if ("enum" in format) {
    const key = format.enum[String(value)];
    return key ? t(key) : String(value);
  }

  if ("resolve" in format) {
    const resolver = context.resolvers[format.resolve];
    return resolver ? resolver(value) : formatScalar(value, t);
  }

  return formatScalar(value, t);
}

function formatScalar(value: unknown, t: TFunction): string {
  if (typeof value === "string") return value || t("common:empty");

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if (typeof obj.name === "string") return obj.name;
  }

  return JSON.stringify(value);
}
